/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 * @author Nikolay Sviridov
 * @since 1.35.0
 */

import { useMemo, useState } from 'react';
import type { FC, SyntheticEvent } from 'react';


import { Typography, debounce } from '@nexign/scc-ui-kit';
import type { TAutocompleteOption } from '@nexign/scc-ui-kit/lib/components/Autocomplete/Autocomplete';
import isNull from 'lodash/isNull';
import { useDispatch, useSelector } from 'react-redux';


import { makeSelectTranslationPageProducts } from '@/main/modules/internationalization/intSelectors';
import { stringFormatParams } from '@/main/utils/stringFormat';

import { colors } from '../../../../theme';
import { clearRoamingOptionsSearchAction, roamingOptionsGetAction, roamingOptionsGetCountriesAction, setSelectedCountryCode } from '../../tariffsAndServicesActions';
import { MINIMAL_SEACRCH_STRING_LENGTH } from '../../tariffsAndServicesConstants';
import { makeSelectProductsRegionSelected, makeSelectRoamingOptionsCountries, makeSelectRoamingOptionsCountriesIsLoading } from '../../tariffsAndServicesSelectors';
import { AutocompleteControl, RoamingControlsStyled, RoamingSearchContainerStyled } from '../styled/RoamingOptionsStyled';

export const RoamingOptionsControl: FC = () => {
	const dispatch = useDispatch();
    const { value: regionSelected } = useSelector(makeSelectProductsRegionSelected());
	const roamCountriesData = useSelector(makeSelectRoamingOptionsCountries());
	const isRoamCountiesDataFetching = useSelector(makeSelectRoamingOptionsCountriesIsLoading());
	const { controls } = useSelector(makeSelectTranslationPageProducts()).roamingOptions;


	const [existClearButton, setExistClearButton] = useState(false);
	const [searchCountry, setSearchCountry] = useState('');

	const noOptionsText = useMemo(() => {
		if (searchCountry.length < MINIMAL_SEACRCH_STRING_LENGTH) {
			return stringFormatParams(controls.searchHint, { quantity: MINIMAL_SEACRCH_STRING_LENGTH })
		} else {
			return isRoamCountiesDataFetching ? controls.searchInProgress : controls.noOptionsText;
		}
	},[
		controls.noOptionsText, 
		controls.searchHint, 
		controls.searchInProgress, 
		isRoamCountiesDataFetching, 
		searchCountry.length,
	]);
	const options =
		isRoamCountiesDataFetching ||
		searchCountry.length < MINIMAL_SEACRCH_STRING_LENGTH ||
		!roamCountriesData
			? []
			: roamCountriesData?.map(country => ({ value: country.code, label: country.title })) || [];

	const onChange = (
		_event: SyntheticEvent<Element, Event>,
		newValue: string | TAutocompleteOption | Array<string | TAutocompleteOption> | null,
	) => {
		if (newValue && !Array.isArray(newValue) && typeof newValue === 'object') {
			dispatch(setSelectedCountryCode({ selectedCountryCode: String(newValue.value) }));
			dispatch(
				roamingOptionsGetAction({ regionId: regionSelected, selectedCountryCode: String(newValue.value) }),
			);
		}
	};

	const onInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
		if (isNull(_event)) return;
		if (reason === 'clear' || !value.length) {
			setSearchCountry('');
			dispatch(clearRoamingOptionsSearchAction());
			setExistClearButton(false);
			return;
		}

		if (!!value && value.length >= MINIMAL_SEACRCH_STRING_LENGTH && reason !== 'reset') {
			dispatch(roamingOptionsGetCountriesAction({ searchString: value }));
		}

		setSearchCountry(value);

		if (value.length) {
			setExistClearButton(true);
		} else {
			setExistClearButton(false);
			dispatch(clearRoamingOptionsSearchAction());
		}
	};

	const debounceInputChange = debounce(onInputChange);

	return (
		<>
			<Typography variant='h4'>{controls.title}</Typography>
			<RoamingControlsStyled>
				<RoamingSearchContainerStyled>
					<AutocompleteControl
						autoHighlight
						clearOnBlur={false}
						filterOptions={(x: any) => x}
						fullWidth={true}
						loading={isRoamCountiesDataFetching}
						noOptionsText={noOptionsText}
						options={options}
						placeholder={controls.placeholder}
						value={searchCountry}
						withClearButton={existClearButton}
						withSearchField={true}
						withSearchIcon={true}
						onChange={onChange}
						onInputChange={debounceInputChange}
					/>
				</RoamingSearchContainerStyled>
				<Typography color={colors.textGreyColor}>{controls.helpText}</Typography>
			</RoamingControlsStyled>
		</>
	);
};
